import { useEffect, useRef, useState } from 'react';

type CopyFn = (en: string, de: string, hr: string) => string;

export type InviteSharePanelProps = {
	inviteCode: string;
	shareText: string;
	copy: CopyFn;
	showWhatsApp?: boolean;
	showCopyCode?: boolean;
	title?: string;
};

export default function InviteSharePanel({
	inviteCode,
	shareText,
	copy,
	showWhatsApp = true,
	showCopyCode = true,
	title
}: InviteSharePanelProps) {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [copiedWhat, setCopiedWhat] = useState<'code' | 'message' | null>(null);

	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${el.scrollHeight}px`;
	}, [shareText]);

	useEffect(() => {
		if (!copiedWhat) return;
		const t = window.setTimeout(() => setCopiedWhat(null), 2200);
		return () => clearTimeout(t);
	}, [copiedWhat]);

	const copyText = async (text: string, what: 'code' | 'message') => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedWhat(what);
		} catch {
			/* ignore */
		}
	};

	return (
		<div className="alert alert-success py-2 small mb-0" role="status">
			{title && <p className="mb-2 fw-semibold">{title}</p>}
			{showCopyCode && (
				<div className="d-flex flex-wrap align-items-center gap-2 mb-2">
					<span>{copy('Invite code:', 'Einladungscode:', 'Kod pozivnice:')}</span>
					<code className="fw-bold user-select-all">{inviteCode}</code>
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => void copyText(inviteCode, 'code')}
					>
						{copy('Copy code', 'Code kopieren', 'Kopiraj kod')}
					</button>
					{copiedWhat === 'code' && (
						<span className="text-success-emphasis">
							{copy('Code copied', 'Code kopiert', 'Kod kopiran')}
						</span>
					)}
				</div>
			)}
			<textarea
				ref={textareaRef}
				className="form-control form-control-sm mb-2"
				value={shareText}
				readOnly
				rows={1}
				style={{ resize: 'none', overflow: 'hidden' }}
				onFocus={(e) => e.currentTarget.select()}
			/>
			<div className="d-flex flex-wrap align-items-center gap-2">
				<button
					type="button"
					className="btn btn-sm btn-outline-secondary"
					onClick={() => void copyText(shareText, 'message')}
				>
					{copy('Copy message', 'Nachricht kopieren', 'Kopiraj poruku')}
				</button>
				{copiedWhat === 'message' && (
					<span className="text-success-emphasis">
						{copy('Message copied', 'Nachricht kopiert', 'Poruka kopirana')}
					</span>
				)}
				{showWhatsApp && (
					<button
						type="button"
						className="btn btn-sm btn-success"
						onClick={() =>
							window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer')
						}
					>
						<i className="bi bi-whatsapp me-1" aria-hidden />
						{copy('Share on WhatsApp', 'Per WhatsApp teilen', 'Podijeli na WhatsAppu')}
					</button>
				)}
			</div>
		</div>
	);
}
